import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useState,
} from "react";

interface PhoneWidgetContextType {
  isVisible: boolean;
  toggleVisibility: () => void;
  show: () => void;
  hide: () => void;
  isVoiceAvailable: boolean; // Whether voice features are available
  setVoiceAvailable: (available: boolean) => void; // Function to set voice availability
}

const PhoneWidgetContext = createContext<PhoneWidgetContextType | null>(null);

export function usePhoneWidget(): PhoneWidgetContextType {
  const context = useContext(PhoneWidgetContext);
  if (!context) {
    throw new Error("usePhoneWidget must be used within a PhoneWidgetProvider");
  }
  return context;
}

interface PhoneWidgetProviderProps {
  children: ReactNode;
}

export const PhoneWidgetProvider = memo(function PhoneWidgetProvider({
  children,
}: PhoneWidgetProviderProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isVoiceAvailable, setVoiceAvailable] = useState(false);

  const toggleVisibility = useCallback(() => setIsVisible((prev) => !prev), []);
  const show = useCallback(() => setIsVisible(true), []);
  const hide = useCallback(() => setIsVisible(false), []);
  const setVoiceAvailableCallback = useCallback(
    (available: boolean) => setVoiceAvailable(available),
    [],
  );

  return (
    <PhoneWidgetContext.Provider
      value={{
        isVisible,
        toggleVisibility,
        show,
        hide,
        isVoiceAvailable,
        setVoiceAvailable: setVoiceAvailableCallback,
      }}
    >
      {children}
    </PhoneWidgetContext.Provider>
  );
});
